import { useState, useEffect, useCallback, useRef } from 'react'
import {
  ArrowLeft, Plus, CheckCircle, XCircle, Loader2, Eye, EyeOff,
  Pencil, Trash2, Signal, ChevronDown, ChevronUp, ArrowRightLeft, ShieldCheck,
  Wifi, Globe, KeyRound, Terminal, AlertTriangle, Download, RefreshCw, Copy,
  CheckCheck, Pause, Play, ChevronRight, Activity, WifiOff, HardDrive,
} from 'lucide-react'
import type { ClientProfile } from '@veridia/shared'
import { useProxyConfig } from '@/hooks/useProxyConfig'
import { TotpVerifyModal } from '@/components/shared/TotpVerifyModal'
import { ProviderTotpManager } from '@/components/shared/ProviderTotpManager'
import { ProviderBackupCodesManager } from '@/components/shared/ProviderBackupCodesManager'
import { LocalDatabaseToggle } from '@/components/shared/LocalDatabaseToggle'
import {
  listProfiles, createProfile, updateProfile, deleteProfile,
  activateProfile, testProfile, relaunch,
} from '@/api/profiles'

interface ProviderModeProps {
  onBack: () => void
}

interface TestState {
  status: 'idle' | 'testing' | 'ok' | 'error'
  error?: string
  latencyMs?: number
}

interface ProfileForm {
  name: string
  databaseUrl: string
  showUrl: boolean
  testState: TestState
  saving: boolean
  error: string | null
  testedUrl: string | null
}

function emptyForm(): ProfileForm {
  return { name: '', databaseUrl: '', showUrl: false, testState: { status: 'idle' }, saving: false, error: null, testedUrl: null }
}

type MigrationEntry = { name: string; status: 'applied' | 'pending'; appliedAt?: string }
type MigrationRunEntry = { name: string; status: 'applied' | 'skipped' | 'failed'; error?: string }

type MqttRole = 'local' | 'remote'

interface MqttCredsState {
  configured: boolean
  url: string
  username: string
  password: string
  showPassword: boolean
  saving: boolean
  error: string | null
  saved: boolean
}

function emptyMqttCreds(): MqttCredsState {
  return { configured: false, url: '', username: '', password: '', showPassword: false, saving: false, error: null, saved: false }
}

export function ProviderMode({ onBack }: ProviderModeProps) {
  const { proxyUrl, proxyToken } = useProxyConfig()
  const headers: Record<string, string> = proxyToken ? { Authorization: `Bearer ${proxyToken}` } : {}

  const [profiles, setProfiles] = useState<ClientProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [profileError, setProfileError] = useState<string | null>(null)

  const [confirmActivate, setConfirmActivate] = useState<ClientProfile | null>(null)
  const [activating, setActivating] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<ClientProfile | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [testStates, setTestStates] = useState<Record<string, TestState>>({})

  const [addForm, setAddForm] = useState<ProfileForm | null>(null)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState<ProfileForm | null>(null)

  const [showMigrations, setShowMigrations] = useState(false)
  const [showAuth, setShowAuth] = useState(false)
  const [showLogs, setShowLogs] = useState(false)
  const [showTotpModal, setShowTotpModal] = useState(false)
  const [showTopology, setShowTopology] = useState(false)
  const [showLocalDb, setShowLocalDb] = useState(false)

  type Topology = 'local' | 'remote'
  const [topology, setTopology] = useState<Topology>('local')
  const [topologyLoading, setTopologyLoading] = useState(false)
  const [topologySaving, setTopologySaving] = useState(false)
  const [topologyError, setTopologyError] = useState<string | null>(null)

  const [showMqtt, setShowMqtt] = useState(false)
  const [mqttLoading, setMqttLoading] = useState(false)
  const [mqttLocalCreds, setMqttLocalCreds] = useState<MqttCredsState>(emptyMqttCreds())
  const [mqttRemoteCreds, setMqttRemoteCreds] = useState<MqttCredsState>(emptyMqttCreds())

  const [migrStatus, setMigrStatus] = useState<{ total: number; applied: number; pending: number; lastAppliedAt: string | null; migrations: MigrationEntry[] } | null>(null)
  const [migrLoading, setMigrLoading] = useState(false)
  const [runResults, setRunResults] = useState<MigrationRunEntry[] | null>(null)
  const [running, setRunning] = useState(false)
  const [runError, setRunError] = useState<string | null>(null)

  useEffect(() => { loadProfiles() }, [proxyUrl, proxyToken])

  async function loadProfiles() {
    if (!proxyUrl) return
    setLoading(true)
    try {
      const list = await listProfiles(proxyUrl, proxyToken)
      setProfiles(list)
    } catch {
      setProfileError('Error al cargar perfiles')
    } finally {
      setLoading(false)
    }
  }

  async function handleTest(id: string) {
    if (!proxyUrl) return
    setTestStates(s => ({ ...s, [id]: { status: 'testing' } }))
    const result = await testProfile(proxyUrl, proxyToken, id)
    setTestStates(s => ({
      ...s,
      [id]: result.success
        ? { status: 'ok', latencyMs: result.latencyMs }
        : { status: 'error', error: result.error },
    }))
  }

  async function handleActivate() {
    if (!proxyUrl || !confirmActivate) return
    setActivating(true)
    try {
      await activateProfile(proxyUrl, proxyToken, confirmActivate.id)
      setConfirmActivate(null)
      await relaunch(proxyUrl, proxyToken)
    } catch (err: any) {
      setProfileError(err.message ?? 'Error al activar perfil')
      setActivating(false)
    }
  }

  async function handleDelete() {
    if (!proxyUrl || !confirmDelete) return
    setDeleting(true)
    try {
      await deleteProfile(proxyUrl, proxyToken, confirmDelete.id)
      setConfirmDelete(null)
      await loadProfiles()
    } catch (err: any) {
      setProfileError(err.message ?? 'Error al eliminar perfil')
    } finally {
      setDeleting(false)
    }
  }

  async function testFormUrl(url: string, form: ProfileForm, setForm: (f: ProfileForm) => void) {
    if (!proxyUrl || !url.trim()) return
    setForm({ ...form, testState: { status: 'testing' } })
    const start = Date.now()
    try {
      const r = await fetch(`${proxyUrl}/local/test-connection`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
        signal: AbortSignal.timeout(6000),
      })
      const data = await r.json() as { success: boolean; error?: string }
      if (data.success) {
        setForm({ ...form, testState: { status: 'ok', latencyMs: Date.now() - start }, testedUrl: url })
      } else {
        setForm({ ...form, testState: { status: 'error', error: data.error ?? 'Error de conexión' }, testedUrl: null })
      }
    } catch {
      setForm({ ...form, testState: { status: 'error', error: 'Sin respuesta del servidor' }, testedUrl: null })
    }
  }

  async function handleAddSave(confirmed = false) {
    if (!proxyUrl || !addForm) return
    if (!confirmed && addForm.testedUrl !== addForm.databaseUrl) {
      if (!window.confirm('No has probado la conexión. ¿Guardar de todas formas?')) return
    }
    setAddForm({ ...addForm, saving: true, error: null })
    try {
      await createProfile(proxyUrl, proxyToken, addForm.name.trim(), addForm.databaseUrl)
      setAddForm(null)
      await loadProfiles()
    } catch (err: any) {
      setAddForm({ ...addForm, saving: false, error: err.message ?? 'Error al guardar' })
    }
  }

  function startEdit(profile: ClientProfile) {
    setEditingId(profile.id)
    setEditForm({ ...emptyForm(), name: profile.name })
  }

  async function handleEditSave(confirmed = false) {
    if (!proxyUrl || !editingId || !editForm) return
    const updates: { name?: string; databaseUrl?: string } = {}
    if (editForm.name.trim()) updates.name = editForm.name.trim()
    if (editForm.databaseUrl) {
      if (!confirmed && editForm.testedUrl !== editForm.databaseUrl) {
        if (!window.confirm('No has probado la nueva URL. ¿Guardar de todas formas?')) return
      }
      updates.databaseUrl = editForm.databaseUrl
    }
    setEditForm({ ...editForm, saving: true, error: null })
    try {
      await updateProfile(proxyUrl, proxyToken, editingId, updates)
      setEditingId(null)
      setEditForm(null)
      await loadProfiles()
    } catch (err: any) {
      setEditForm({ ...editForm, saving: false, error: err.message ?? 'Error al actualizar' })
    }
  }

  useEffect(() => {
    if (!showTopology || !proxyUrl) return
    loadTopology()
  }, [showTopology, proxyUrl])

  async function loadTopology() {
    if (!proxyUrl) return
    setTopologyLoading(true)
    setTopologyError(null)
    try {
      const r = await fetch(`${proxyUrl}/local/network-topology`, { headers })
      const data = await r.json() as { topology?: Topology }
      setTopology(data.topology ?? 'local')
    } catch {
      setTopologyError('Error al cargar la configuración de red')
    } finally {
      setTopologyLoading(false)
    }
  }

  async function saveTopology(value: Topology) {
    if (!proxyUrl) return
    setTopologySaving(true)
    setTopologyError(null)
    try {
      await fetch(`${proxyUrl}/local/network-topology`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ topology: value }),
      })
      setTopology(value)
    } catch {
      setTopologyError('Error al guardar la configuración de red')
    } finally {
      setTopologySaving(false)
    }
  }

  useEffect(() => {
    if (!showMqtt || !proxyUrl) return
    loadMqttCredentials()
  }, [showMqtt, proxyUrl])

  async function loadMqttCredentials() {
    if (!proxyUrl) return
    setMqttLoading(true)
    try {
      const [localRes, remoteRes] = await Promise.all([
        fetch(`${proxyUrl}/local/mqtt-credentials?role=local`, { headers }).then(r => r.json()),
        fetch(`${proxyUrl}/local/mqtt-credentials?role=remote`, { headers }).then(r => r.json()),
      ]) as [{ configured: boolean; url: string | null; username: string | null }, { configured: boolean; url: string | null; username: string | null }]
      setMqttLocalCreds(s => ({ ...s, configured: localRes.configured, url: localRes.url ?? '', username: localRes.username ?? '' }))
      setMqttRemoteCreds(s => ({ ...s, configured: remoteRes.configured, url: remoteRes.url ?? '', username: remoteRes.username ?? '' }))
    } catch {
      // non-fatal — forms stay editable even if the initial load fails
    } finally {
      setMqttLoading(false)
    }
  }

  async function saveMqttCredentials(role: MqttRole, state: MqttCredsState, setState: (s: MqttCredsState) => void) {
    if (!proxyUrl) return
    setState({ ...state, saving: true, error: null, saved: false })
    try {
      const r = await fetch(`${proxyUrl}/local/mqtt-credentials`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ role, url: state.url, username: state.username, password: state.password }),
      })
      const data = await r.json() as { success?: boolean; error?: string }
      if (!r.ok || !data.success) throw new Error(data.error ?? 'Error al guardar')
      setState({ ...state, saving: false, configured: true, password: '', saved: true })
    } catch (err: any) {
      setState({ ...state, saving: false, error: err.message ?? 'Error al guardar' })
    }
  }

  useEffect(() => {
    if (!showMigrations || !proxyUrl) return
    loadMigrationStatus()
  }, [showMigrations, proxyUrl])

  function loadMigrationStatus() {
    if (!proxyUrl) return
    setMigrLoading(true)
    fetch(`${proxyUrl}/local/migrations/status`, { headers })
      .then(r => r.json())
      .then((d: any) => setMigrStatus(d?.migrations ? d : null))
      .catch(() => {})
      .finally(() => setMigrLoading(false))
  }

  async function handleRunMigrations(opToken?: string) {
    if (!proxyUrl) return
    setRunning(true); setRunResults(null); setRunError(null)
    try {
      const extraHeaders: Record<string, string> = opToken ? { 'X-Op-Token': opToken } : {}
      const r = await fetch(`${proxyUrl}/local/migrations/run`, {
        method: 'POST', headers: { ...headers, ...extraHeaders },
      })
      if (r.status === 401 && !opToken) {
        setShowTotpModal(true)
        return
      }
      const data = await r.json() as { results?: MigrationRunEntry[]; error?: string }
      if (data.results) setRunResults(data.results)
      if (data.error) setRunError(data.error)
      loadMigrationStatus()
    } catch {
      setRunError('Error de red al ejecutar migraciones')
    } finally {
      setRunning(false)
    }
  }

  return (
    <div
      className="min-h-screen text-white flex flex-col relative"
      style={{ background: 'linear-gradient(135deg, #0c1c2e 0%, #0b2d1f 45%, #0e1a32 100%)' }}
    >
      {/* Background orbs */}
      <div className="fixed -top-40 -right-40 w-[500px] h-[500px] rounded-full blur-3xl pointer-events-none"
           style={{ background: 'radial-gradient(circle, hsl(160 84% 39% / 0.2) 0%, transparent 70%)' }} />
      <div className="fixed -bottom-40 -left-20 w-[400px] h-[400px] rounded-full blur-3xl pointer-events-none"
           style={{ background: 'radial-gradient(circle, #4f46e5 0%, transparent 70%)', opacity: 0.15 }} />

      {/* Header */}
      <div className="relative border-b border-white/8 bg-white/3 backdrop-blur-sm px-6 py-4 flex items-center gap-4">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-white/50 hover:text-white transition-colors text-sm px-2 py-1.5 rounded-lg hover:bg-white/8"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver
        </button>
        <div className="h-4 w-px bg-white/10" />
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-white/10 border border-white/15 flex items-center justify-center">
            <span className="text-white font-bold text-xs">IS</span>
          </div>
          <span className="font-semibold text-base text-white">Modo Proveedor</span>
        </div>
        <span className="ml-auto inline-flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full font-medium">
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
          TecnoInnovate
        </span>
      </div>

      <div className="relative flex-1 overflow-auto">
        <div className="max-w-2xl mx-auto px-6 py-8 flex flex-col gap-8">

          {/* Profiles section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-white font-semibold text-base">Perfiles de cliente</h2>
                <p className="text-white/35 text-xs mt-0.5">Conexiones a bases de datos configuradas</p>
              </div>
              <button
                onClick={() => setAddForm(emptyForm())}
                className="flex items-center gap-1.5 text-xs font-medium text-white px-3 py-2 rounded-lg border border-white/15 hover:bg-white/8 transition-all"
              >
                <Plus className="w-3.5 h-3.5" />
                Agregar cliente
              </button>
            </div>

            {profileError && (
              <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 mb-3">
                <XCircle className="w-3.5 h-3.5 shrink-0" />
                {profileError}
              </div>
            )}

            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-white/25" />
              </div>
            ) : profiles.length === 0 && !addForm ? (
              <div className="border border-dashed border-white/10 rounded-2xl p-10 text-center">
                <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mx-auto mb-4">
                  <Plus className="w-5 h-5 text-white/30" />
                </div>
                <p className="text-white/40 text-sm mb-4">No hay perfiles configurados</p>
                <button
                  onClick={() => setAddForm(emptyForm())}
                  className="text-xs font-medium text-white/60 hover:text-white border border-white/15 hover:border-white/30 px-3 py-1.5 rounded-lg transition-all"
                >
                  + Agregar el primer cliente
                </button>
              </div>
            ) : (
              <div className="flex flex-col gap-2.5">
                {profiles.map(profile => (
                  <ProfileCard
                    key={profile.id}
                    profile={profile}
                    testState={testStates[profile.id] ?? { status: 'idle' }}
                    isEditing={editingId === profile.id}
                    editForm={editingId === profile.id ? editForm : null}
                    onActivate={() => setConfirmActivate(profile)}
                    onTest={() => handleTest(profile.id)}
                    onEdit={() => startEdit(profile)}
                    onDelete={() => setConfirmDelete(profile)}
                    onEditFormChange={setEditForm}
                    onTestUrl={(url) => editForm && testFormUrl(url, editForm, setEditForm)}
                    onEditSave={handleEditSave}
                    onEditCancel={() => { setEditingId(null); setEditForm(null) }}
                    isOnlyProfile={profiles.length === 1}
                  />
                ))}
              </div>
            )}

            {addForm && (
              <ProfileFormCard
                form={addForm}
                title="Nuevo perfil"
                onChange={setAddForm}
                onTest={(url) => testFormUrl(url, addForm, setAddForm)}
                onSave={() => handleAddSave()}
                onCancel={() => setAddForm(null)}
              />
            )}
          </div>

          {/* Auth section */}
          <CollapsibleSection
            icon={<ShieldCheck className="w-4 h-4" />}
            title="Autenticación"
            open={showAuth}
            onToggle={() => setShowAuth(v => !v)}
          >
            <div className="flex flex-col gap-6 pt-2">
              <div>
                <p className="text-white/60 text-xs font-medium uppercase tracking-wide mb-3">Dispositivos TOTP</p>
                <ProviderTotpManager />
              </div>
              <div className="border-t border-white/8 pt-4">
                <p className="text-white/60 text-xs font-medium uppercase tracking-wide mb-3">Códigos de respaldo</p>
                <ProviderBackupCodesManager />
              </div>
            </div>
          </CollapsibleSection>

          {/* Network topology section */}
          <CollapsibleSection
            icon={<Globe className="w-4 h-4" />}
            title="Topología de red"
            open={showTopology}
            onToggle={() => setShowTopology(v => !v)}
          >
            <div className="flex flex-col gap-4 pt-2">
              <p className="text-white/40 text-xs leading-relaxed">
                Indica cómo está instalado este equipo en relación a los dispositivos Hikvision. Esto afecta cómo se registran nuevos dispositivos y el uso del MQTT.
              </p>
              {topologyLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-4 h-4 animate-spin text-white/25" />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  <TopologyCard
                    icon={<Wifi className="w-5 h-5" />}
                    value="local"
                    selected={topology === 'local'}
                    saving={topologySaving}
                    title="Red local"
                    description="Este PC está en la misma red Wi-Fi/LAN que los dispositivos. El escaneo automático está disponible."
                    onSelect={saveTopology}
                  />
                  <TopologyCard
                    icon={<Globe className="w-5 h-5" />}
                    value="remote"
                    selected={topology === 'remote'}
                    saving={topologySaving}
                    title="Control remoto"
                    description="Este PC está en otra ubicación y controla el establecimiento vía MQTT. Los dispositivos se registran manualmente."
                    onSelect={saveTopology}
                  />
                </div>
              )}
              {topologyError && (
                <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                  <XCircle className="w-3.5 h-3.5 shrink-0" />
                  {topologyError}
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* Local-first database section */}
          <CollapsibleSection
            icon={<HardDrive className="w-4 h-4" />}
            title="Base de datos local"
            open={showLocalDb}
            onToggle={() => setShowLocalDb(v => !v)}
          >
            {proxyUrl && <LocalDatabaseToggle proxyUrl={proxyUrl} proxyToken={proxyToken} />}
          </CollapsibleSection>

          {/* MQTT credentials section */}
          <CollapsibleSection
            icon={<KeyRound className="w-4 h-4" />}
            title="Credenciales MQTT"
            open={showMqtt}
            onToggle={() => setShowMqtt(v => !v)}
          >
            <div className="flex flex-col gap-4 pt-2">
              <p className="text-white/40 text-xs leading-relaxed">
                Dos credenciales independientes: el rol <span className="text-white/60">local</span> ejecuta comandos contra los dispositivos, el rol <span className="text-white/60">remoto</span> los publica y espera el resultado. Necesarias para que el modo remoto funcione end-to-end.
              </p>
              {mqttLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-4 h-4 animate-spin text-white/25" />
                </div>
              ) : (
                <div className="flex flex-col gap-3">
                  <MqttCredsForm
                    title="Rol local (ejecutor)"
                    description="Usado por las instalaciones en el gimnasio, junto a los dispositivos."
                    state={mqttLocalCreds}
                    onChange={setMqttLocalCreds}
                    onSave={() => saveMqttCredentials('local', mqttLocalCreds, setMqttLocalCreds)}
                  />
                  <MqttCredsForm
                    title="Rol remoto (publicador)"
                    description="Usado por esta consola cuando la topología de red es 'Control remoto'."
                    state={mqttRemoteCreds}
                    onChange={setMqttRemoteCreds}
                    onSave={() => saveMqttCredentials('remote', mqttRemoteCreds, setMqttRemoteCreds)}
                  />
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* Migrations section */}
          <CollapsibleSection
            icon={<ArrowRightLeft className="w-4 h-4" />}
            title="Migraciones"
            open={showMigrations}
            onToggle={() => setShowMigrations(v => !v)}
          >
            <div className="flex flex-col gap-4 pt-2">
              {migrLoading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="w-4 h-4 animate-spin text-white/25" />
                </div>
              ) : !migrStatus ? (
                <p className="text-white/35 text-xs text-center py-4">Configura y activa un perfil de base de datos para ver las migraciones.</p>
              ) : (
                <>
                  {/* Auto-applied notice */}
                  {migrStatus.pending === 0 && migrStatus.applied > 0 && migrStatus.lastAppliedAt && (
                    <div className="flex items-start gap-2 bg-emerald-500/8 border border-emerald-500/20 rounded-xl px-3 py-2.5">
                      <CheckCircle className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-emerald-400 text-xs font-medium">Base de datos al día</p>
                        <p className="text-white/35 text-[10px] mt-0.5">
                          {migrStatus.applied} migraciones aplicadas automáticamente al iniciar la app ·{' '}
                          última: {new Date(migrStatus.lastAppliedAt).toLocaleString('es-CO', {
                            day: '2-digit', month: '2-digit', year: '2-digit',
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </div>
                  )}
                  {migrStatus.pending > 0 && (
                    <div className="flex items-start gap-2 bg-amber-500/8 border border-amber-500/20 rounded-xl px-3 py-2.5">
                      <div className="w-3.5 h-3.5 rounded-full border border-amber-400 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-amber-400 text-xs font-medium">{migrStatus.pending} migración{migrStatus.pending !== 1 ? 'es' : ''} pendiente{migrStatus.pending !== 1 ? 's' : ''}</p>
                        <p className="text-white/35 text-[10px] mt-0.5">
                          Ejecuta las migraciones para actualizar la estructura de la base de datos.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex gap-3">
                    <div className="flex-1 bg-white/5 border border-white/8 rounded-xl px-4 py-3 text-center">
                      <p className="text-2xl font-bold text-white">{migrStatus.applied}</p>
                      <p className="text-xs text-white/35 mt-0.5">Aplicadas</p>
                    </div>
                    <div className={`flex-1 rounded-xl px-4 py-3 text-center border ${
                      migrStatus.pending > 0
                        ? 'bg-amber-500/10 border-amber-500/20'
                        : 'bg-white/5 border-white/8'
                    }`}>
                      <p className={`text-2xl font-bold ${migrStatus.pending > 0 ? 'text-amber-400' : 'text-white'}`}>
                        {migrStatus.pending}
                      </p>
                      <p className="text-xs text-white/35 mt-0.5">Pendientes</p>
                    </div>
                  </div>
                  <div className="max-h-40 overflow-y-auto flex flex-col gap-0.5">
                    {migrStatus.migrations.map(m => (
                      <div key={m.name} className="flex items-center gap-2.5 text-xs py-1.5 px-1">
                        {m.status === 'applied'
                          ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                          : <div className="w-3.5 h-3.5 rounded-full border border-amber-500/60 shrink-0" />
                        }
                        <span className={`font-mono truncate ${m.status === 'applied' ? 'text-white/50' : 'text-white/80'}`}>
                          {m.name}
                        </span>
                        {m.appliedAt && (
                          <span className="text-white/25 ml-auto shrink-0">
                            {new Date(m.appliedAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
              {runResults && (
                <div className="flex flex-col gap-0.5 bg-white/3 rounded-xl p-3">
                  {runResults.map(r => (
                    <div key={r.name} className={`flex items-center gap-2 text-xs py-1 ${r.status === 'failed' ? 'text-red-400' : 'text-white/50'}`}>
                      {r.status === 'applied'  && <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />}
                      {r.status === 'skipped'  && <span className="w-3.5 shrink-0 text-center">↷</span>}
                      {r.status === 'failed'   && <XCircle className="w-3.5 h-3.5 shrink-0" />}
                      <span className="font-mono truncate">{r.name}</span>
                      {r.error && <span className="text-red-400 ml-2 truncate">{r.error}</span>}
                    </div>
                  ))}
                </div>
              )}
              {runError && (
                <div className="flex items-center gap-2 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
                  <XCircle className="w-3.5 h-3.5 shrink-0" />
                  {runError}
                </div>
              )}
              <button
                onClick={() => handleRunMigrations()}
                disabled={running}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-white text-sm font-semibold transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)' }}
              >
                {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRightLeft className="w-4 h-4" />}
                {running ? 'Ejecutando...' : 'Ejecutar migraciones'}
              </button>
            </div>
          </CollapsibleSection>

          {/* System Logs section — provider-only diagnostic tool */}
          <CollapsibleSection
            icon={<Terminal className="w-4 h-4" />}
            title="Logs del Sistema"
            open={showLogs}
            onToggle={() => setShowLogs(v => !v)}
          >
            <SystemLogsSection proxyUrl={proxyUrl} proxyToken={proxyToken} />
          </CollapsibleSection>
        </div>
      </div>

      {/* Confirm activate dialog */}
      {confirmActivate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#0f1e30] border border-white/10 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-white font-semibold text-base mb-2">¿Cambiar a {confirmActivate.name}?</h3>
            <p className="text-white/50 text-sm mb-6 leading-relaxed">
              La app se reiniciará para aplicar el cambio. El proceso toma unos segundos.
            </p>
            <div className="flex gap-2.5">
              <button
                onClick={() => setConfirmActivate(null)}
                className="flex-1 border border-white/12 text-white/60 hover:text-white hover:bg-white/8 text-sm py-2.5 rounded-xl transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleActivate}
                disabled={activating}
                className="flex-1 flex items-center justify-center gap-1.5 text-white text-sm font-semibold py-2.5 rounded-xl transition-all active:scale-[0.98] disabled:opacity-40"
                style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)' }}
              >
                {activating && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Cambiar y reiniciar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete dialog */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#0f1e30] border border-white/10 rounded-2xl p-6 w-full max-w-sm shadow-2xl">
            <h3 className="text-white font-semibold text-base mb-2">¿Eliminar {confirmDelete.name}?</h3>
            <p className="text-white/50 text-sm mb-6">Esta acción no se puede deshacer.</p>
            <div className="flex gap-2.5">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 border border-white/12 text-white/60 hover:text-white hover:bg-white/8 text-sm py-2.5 rounded-xl transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 flex items-center justify-center gap-1.5 bg-red-500/15 border border-red-500/30 hover:bg-red-500/25 text-red-400 hover:text-red-300 text-sm font-semibold py-2.5 rounded-xl transition-all active:scale-[0.98] disabled:opacity-40"
              >
                {deleting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {showTotpModal && (
        <TotpVerifyModal
          actionLabel="Verificar y ejecutar"
          onVerified={(opToken) => { setShowTotpModal(false); handleRunMigrations(opToken) }}
          onCancel={() => setShowTotpModal(false)}
        />
      )}
    </div>
  )
}

// ── SystemLogsSection ────────────────────────────────────────────────────────

type LogLevel    = 'error' | 'warn' | 'info' | 'debug'
type LogCategory = 'system' | 'sync' | 'devices' | 'db' | 'auth' | 'proxy' | 'mqtt' | 'updater'

interface LogEntry {
  id: string
  timestamp: string
  level: LogLevel
  category: LogCategory
  message: string
  details?: string
}

interface LogStats {
  totalEntries: number
  errors24h: number
  warnings24h: number
  interventionRequired: boolean
  interventionReasons: string[]
}

const LOG_LEVEL_CFG: Record<LogLevel, { color: string; label: string }> = {
  error: { color: 'text-red-400',   label: 'ERR' },
  warn:  { color: 'text-amber-400', label: 'WRN' },
  info:  { color: 'text-sky-400',   label: 'INF' },
  debug: { color: 'text-white/30',  label: 'DBG' },
}
const LOG_CAT_LABELS: Record<LogCategory, string> = {
  system: 'Sistema', sync: 'Sync', devices: 'Dispositivos',
  db: 'BD', auth: 'Auth', proxy: 'Proxy', mqtt: 'MQTT', updater: 'Updates',
}
const LOG_CAT_COLORS: Record<LogCategory, string> = {
  system: 'text-violet-400', sync: 'text-emerald-400', devices: 'text-cyan-400',
  db: 'text-yellow-400', auth: 'text-pink-400', proxy: 'text-indigo-400',
  mqtt: 'text-orange-400', updater: 'text-teal-400',
}

function LogEntryRow({ entry }: { entry: LogEntry }) {
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState(false)
  const cfg = LOG_LEVEL_CFG[entry.level]
  const ts = new Date(entry.timestamp)
  const timeStr = ts.toLocaleTimeString('es-CO', { hour12: false })

  const handleCopy = () => {
    const text = `[${entry.timestamp}] [${entry.level}] [${entry.category}] ${entry.message}${entry.details ? '\n' + entry.details : ''}`
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500) })
  }

  return (
    <div className={`border-b border-white/5 group ${expanded ? 'bg-white/3' : 'hover:bg-white/2'}`}>
      <div className="flex items-start gap-2 px-3 py-1.5 cursor-pointer" onClick={() => entry.details && setExpanded(e => !e)}>
        <span className={`text-[10px] font-bold font-mono shrink-0 mt-0.5 w-7 ${cfg.color}`}>{cfg.label}</span>
        <span className="text-white/20 text-[10px] font-mono shrink-0 mt-0.5 w-16">{timeStr}</span>
        <span className={`text-[10px] font-bold shrink-0 mt-0.5 w-16 ${LOG_CAT_COLORS[entry.category]}`}>{LOG_CAT_LABELS[entry.category]}</span>
        <span className="text-white/65 text-[10px] font-mono flex-1 leading-relaxed break-words min-w-0">{entry.message}</span>
        <div className="flex gap-0.5 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
          <button onClick={e => { e.stopPropagation(); handleCopy() }} className="p-0.5 text-white/20 hover:text-white/60 transition-colors">
            {copied ? <CheckCheck className="w-2.5 h-2.5 text-emerald-400" /> : <Copy className="w-2.5 h-2.5" />}
          </button>
          {entry.details && (
            <span className="text-white/15 p-0.5">
              {expanded ? <ChevronDown className="w-2.5 h-2.5" /> : <ChevronRight className="w-2.5 h-2.5" />}
            </span>
          )}
        </div>
      </div>
      {expanded && entry.details && (
        <div className="mx-3 mb-2 bg-black/40 border border-white/8 rounded-lg p-2">
          <pre className="text-[9px] text-white/40 font-mono whitespace-pre-wrap break-words leading-relaxed max-h-32 overflow-y-auto">{entry.details}</pre>
        </div>
      )}
    </div>
  )
}

function SystemLogsSection({ proxyUrl, proxyToken }: { proxyUrl: string | null; proxyToken: string | null }) {
  const [entries, setEntries]   = useState<LogEntry[]>([])
  const [stats, setStats]       = useState<LogStats | null>(null)
  const [loading, setLoading]   = useState(true)
  const [live, setLive]         = useState(true)
  const [levelFilter, setLevel] = useState<LogLevel | ''>('')
  const [search, setSearch]     = useState('')
  const [summaryCopied, setSummaryCopied] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const authHeaders = useCallback((): HeadersInit => ({
    'x-proxy-token': proxyToken ?? '',
    'Content-Type': 'application/json',
  }), [proxyToken])

  const load = useCallback(async () => {
    if (!proxyUrl) return
    try {
      const params = new URLSearchParams({ limit: '300' })
      if (levelFilter) params.set('level', levelFilter)
      if (search)      params.set('search', search)
      const [logsRes, statsRes] = await Promise.all([
        fetch(`${proxyUrl}/local/logs?${params}`, { headers: authHeaders() }).then(r => r.json()) as Promise<{ entries: LogEntry[] }>,
        fetch(`${proxyUrl}/local/logs/stats`, { headers: authHeaders() }).then(r => r.json()) as Promise<LogStats>,
      ])
      setEntries(logsRes.entries)
      setStats(statsRes)
    } catch { /* offline */ }
    finally { setLoading(false) }
  }, [proxyUrl, authHeaders, levelFilter, search])

  useEffect(() => {
    load()
    if (live) { intervalRef.current = setInterval(load, 2000) }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [load, live])

  const handleClear = async () => {
    if (!proxyUrl || !confirm('¿Limpiar todos los logs?')) return
    await fetch(`${proxyUrl}/local/logs`, { method: 'DELETE', headers: authHeaders() })
    setEntries([]); setStats(null); load()
  }

  const handleCopySummary = () => {
    const lines = [
      `=== Diagnóstico Veridia — ${new Date().toLocaleString('es-CO')} ===`,
      stats ? `Errores 24h: ${stats.errors24h} | Advertencias: ${stats.warnings24h} | Buffer: ${stats.totalEntries}` : '',
      stats?.interventionRequired
        ? `⚠ INTERVENCIÓN:\n${stats.interventionReasons.map(r => `  • ${r}`).join('\n')}`
        : '✓ Sin alertas',
      '', '=== Últimos errores ===',
      ...entries.filter(e => e.level === 'error').slice(0, 15).map(
        e => `[${e.timestamp}] [${e.category}] ${e.message}${e.details ? '\n  → ' + e.details.slice(0, 150) : ''}`,
      ),
    ]
    navigator.clipboard.writeText(lines.join('\n')).then(() => { setSummaryCopied(true); setTimeout(() => setSummaryCopied(false), 2000) })
  }

  const downloadHref = proxyUrl ? `${proxyUrl}/local/logs/download` : '#'

  return (
    <div className="flex flex-col gap-3 pt-1">
      {/* Intervention banner */}
      {stats?.interventionRequired && (
        <div className="bg-red-500/10 border border-red-500/25 rounded-xl px-3 py-3">
          <div className="flex items-center gap-2 mb-1.5">
            <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" />
            <span className="text-red-300 font-bold text-xs">Intervención requerida</span>
          </div>
          <ul className="flex flex-col gap-0.5">
            {stats.interventionReasons.map((r, i) => (
              <li key={i} className="text-red-300/70 text-[10px] flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-red-400 shrink-0" />{r}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: 'Errores 24h',  value: stats.errors24h,   color: stats.errors24h   > 0 ? 'text-red-400'   : 'text-white' },
            { label: 'Warnings 24h', value: stats.warnings24h, color: stats.warnings24h > 0 ? 'text-amber-400' : 'text-white' },
            { label: 'Buffer total', value: stats.totalEntries, color: 'text-white' },
          ].map(m => (
            <div key={m.label} className="bg-white/4 border border-white/8 rounded-xl px-3 py-2 text-center">
              <p className={`text-lg font-bold ${m.color}`}>{m.value}</p>
              <p className="text-white/30 text-[10px]">{m.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-1.5">
        <input
          type="text"
          placeholder="Buscar..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-[10px] text-white/70 placeholder:text-white/20 outline-none focus:border-violet-500/40 transition-all"
        />
        <select
          value={levelFilter}
          onChange={e => setLevel(e.target.value as LogLevel | '')}
          className="bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-[10px] text-white/60 outline-none transition-all cursor-pointer"
        >
          <option value="">Todos</option>
          <option value="error">🔴 Error</option>
          <option value="warn">🟡 Warn</option>
          <option value="info">🔵 Info</option>
        </select>
        <button
          onClick={() => setLive(l => !l)}
          title={live ? 'Pausar' : 'En vivo'}
          className={`p-1.5 rounded-lg border transition-all text-[10px] flex items-center gap-1 ${live ? 'bg-emerald-500/15 border-emerald-500/25 text-emerald-400' : 'bg-white/5 border-white/10 text-white/30'}`}
        >
          {live ? <><Activity className="w-3 h-3" />Vivo</> : <><Play className="w-3 h-3" />Pausado</>}
        </button>
        <button onClick={load} title="Refrescar" className="p-1.5 rounded-lg text-white/30 hover:text-white/60 hover:bg-white/8 border border-transparent hover:border-white/10 transition-all">
          <RefreshCw className="w-3 h-3" />
        </button>
        <a href={downloadHref} download title="Descargar .log" className="p-1.5 rounded-lg text-white/30 hover:text-sky-400 hover:bg-sky-500/10 border border-transparent hover:border-sky-500/20 transition-all">
          <Download className="w-3 h-3" />
        </a>
        <button onClick={handleCopySummary} title="Copiar resumen para soporte" className="p-1.5 rounded-lg text-white/30 hover:text-violet-400 hover:bg-violet-500/10 border border-transparent hover:border-violet-500/20 transition-all">
          {summaryCopied ? <CheckCheck className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />}
        </button>
        <button onClick={handleClear} title="Limpiar" className="p-1.5 rounded-lg text-white/30 hover:text-red-400 hover:bg-red-500/10 border border-transparent hover:border-red-500/20 transition-all">
          <Trash2 className="w-3 h-3" />
        </button>
      </div>

      {/* Log list */}
      <div className="bg-black/30 border border-white/8 rounded-xl overflow-hidden">
        <div className="max-h-80 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8 gap-2">
              <Loader2 className="w-3.5 h-3.5 text-white/25 animate-spin" />
              <span className="text-white/25 text-xs">Cargando...</span>
            </div>
          ) : entries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 gap-1">
              <Terminal className="w-5 h-5 text-white/10" />
              <span className="text-white/20 text-xs">Sin logs</span>
            </div>
          ) : (
            entries.map(e => <LogEntryRow key={e.id} entry={e} />)
          )}
        </div>
        <div className="border-t border-white/6 px-3 py-1.5 flex items-center justify-between">
          <span className="text-white/15 text-[9px]">{entries.length} entradas</span>
          <span className={`text-[9px] flex items-center gap-1 ${live ? 'text-emerald-400' : 'text-white/15'}`}>
            {live ? <><Wifi className="w-2.5 h-2.5" />Auto 2s</> : <><WifiOff className="w-2.5 h-2.5" />Pausado</>}
          </span>
        </div>
      </div>
    </div>
  )
}

// ── Sub-components ──────────────────────────────────────────────────────────

interface ProfileCardProps {
  profile: ClientProfile
  testState: TestState
  isEditing: boolean
  editForm: ProfileForm | null
  isOnlyProfile: boolean
  onActivate: () => void
  onTest: () => void
  onEdit: () => void
  onDelete: () => void
  onEditFormChange: (f: ProfileForm) => void
  onTestUrl: (url: string) => void
  onEditSave: () => void
  onEditCancel: () => void
}

function ProfileCard({
  profile, testState, isEditing, editForm, isOnlyProfile,
  onActivate, onTest, onEdit, onDelete,
  onEditFormChange, onTestUrl, onEditSave, onEditCancel,
}: ProfileCardProps) {
  return (
    <div className={`border rounded-2xl p-4 transition-all ${
      profile.isActive
        ? 'border-emerald-500/30 bg-emerald-500/8'
        : 'border-white/8 bg-white/4 hover:bg-white/6'
    }`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-sm text-white truncate">{profile.name}</span>
            {profile.isActive && (
              <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 px-2 py-0.5 rounded-full font-medium shrink-0">
                <div className="w-1 h-1 rounded-full bg-emerald-400" />
                Activo
              </span>
            )}
          </div>
          {profile.databaseUrlPreview && (
            <span className="font-mono text-xs text-white/30">{profile.databaseUrlPreview}</span>
          )}
          <div className="mt-1 text-xs text-white/20">
            Último uso: {profile.lastUsedAt ? new Date(profile.lastUsedAt).toLocaleDateString() : '—'}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={onTest}
            disabled={testState.status === 'testing'}
            title="Probar conexión"
            className="p-2 text-white/30 hover:text-blue-400 hover:bg-blue-500/10 rounded-lg transition-all disabled:opacity-30"
          >
            {testState.status === 'testing'
              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
              : <Signal className="w-3.5 h-3.5" />
            }
          </button>
          <button
            onClick={onEdit}
            title="Editar"
            className="p-2 text-white/30 hover:text-white/80 hover:bg-white/8 rounded-lg transition-all"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={onDelete}
            disabled={isOnlyProfile && profile.isActive}
            title={isOnlyProfile && profile.isActive ? 'No puedes eliminar el único perfil activo' : 'Eliminar'}
            className="p-2 text-white/30 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all disabled:opacity-20 disabled:cursor-not-allowed"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          {!profile.isActive && (
            <button
              onClick={onActivate}
              className="ml-1 text-xs font-medium text-white px-3 py-1.5 rounded-lg transition-all active:scale-[0.97]"
              style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)' }}
            >
              Activar
            </button>
          )}
        </div>
      </div>

      {testState.status === 'ok' && (
        <div className="flex items-center gap-1.5 text-emerald-400 text-xs mt-2.5 bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-2.5 py-1.5">
          <CheckCircle className="w-3 h-3" />
          Conexión exitosa{testState.latencyMs ? ` · ${testState.latencyMs}ms` : ''}
        </div>
      )}
      {testState.status === 'error' && (
        <div className="flex items-center gap-1.5 text-red-400 text-xs mt-2.5 bg-red-500/10 border border-red-500/20 rounded-lg px-2.5 py-1.5">
          <XCircle className="w-3 h-3" />
          {testState.error ?? 'Error de conexión'}
        </div>
      )}

      {isEditing && editForm && (
        <div className="mt-4 pt-4 border-t border-white/8">
          <ProfileFormCard
            form={editForm}
            title="Editar perfil"
            compact
            onChange={onEditFormChange}
            onTest={onTestUrl}
            onSave={onEditSave}
            onCancel={onEditCancel}
          />
        </div>
      )}
    </div>
  )
}

interface ProfileFormCardProps {
  form: ProfileForm
  title: string
  compact?: boolean
  onChange: (f: ProfileForm) => void
  onTest: (url: string) => void
  onSave: () => void
  onCancel: () => void
}

function ProfileFormCard({ form, title, compact, onChange, onTest, onSave, onCancel }: ProfileFormCardProps) {
  const wrapClass = compact ? '' : 'border border-white/10 bg-white/4 rounded-2xl p-4 mt-2.5'
  return (
    <div className={wrapClass}>
      {!compact && <p className="text-sm font-semibold text-white mb-4">{title}</p>}
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <label className="text-white/40 text-xs font-medium uppercase tracking-wide">Nombre del cliente</label>
          <input
            type="text"
            value={form.name}
            onChange={e => onChange({ ...form, name: e.target.value })}
            placeholder="Ej: Gym Norte"
            className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent transition-all"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-white/40 text-xs font-medium uppercase tracking-wide">URL de base de datos</label>
          <div className="relative">
            <input
              type={form.showUrl ? 'text' : 'password'}
              value={form.databaseUrl}
              onChange={e => onChange({ ...form, databaseUrl: e.target.value, testState: { status: 'idle' }, testedUrl: null })}
              placeholder="postgresql://user:pass@host:5432/db"
              className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 pr-9 text-sm text-white placeholder:text-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent font-mono transition-all"
            />
            <button
              type="button"
              onClick={() => onChange({ ...form, showUrl: !form.showUrl })}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70 transition-colors"
            >
              {form.showUrl ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            </button>
          </div>
        </div>

        {form.testState.status === 'ok' && (
          <div className="flex items-center gap-1.5 text-emerald-400 text-xs bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-2.5 py-1.5">
            <CheckCircle className="w-3 h-3" />
            Conexión exitosa{form.testState.latencyMs ? ` · ${form.testState.latencyMs}ms` : ''}
          </div>
        )}
        {form.testState.status === 'error' && (
          <div className="flex items-center gap-1.5 text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-2.5 py-1.5">
            <XCircle className="w-3 h-3" />
            {form.testState.error}
          </div>
        )}
        {form.error && (
          <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-2.5 py-1.5">{form.error}</p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            onClick={() => onTest(form.databaseUrl)}
            disabled={form.testState.status === 'testing' || !form.databaseUrl.trim()}
            className="flex-1 flex items-center justify-center gap-1.5 border border-white/12 text-white/60 hover:text-white hover:bg-white/8 disabled:opacity-30 text-xs font-medium py-2 rounded-xl transition-all"
          >
            {form.testState.status === 'testing'
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <Signal className="w-3 h-3" />
            }
            {form.testState.status === 'testing' ? 'Probando...' : 'Probar'}
          </button>
          <button
            onClick={onSave}
            disabled={form.saving || !form.name.trim()}
            className="flex-1 flex items-center justify-center gap-1.5 text-white text-xs font-semibold py-2 rounded-xl transition-all active:scale-[0.97] disabled:opacity-40"
            style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)' }}
          >
            {form.saving && <Loader2 className="w-3 h-3 animate-spin" />}
            Guardar
          </button>
          <button
            onClick={onCancel}
            className="px-3 text-white/30 hover:text-white/70 text-xs transition-colors"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  )
}

interface TopologyCardProps {
  icon: React.ReactNode
  value: 'local' | 'remote'
  selected: boolean
  saving: boolean
  title: string
  description: string
  onSelect: (v: 'local' | 'remote') => void
}

function TopologyCard({ icon, value, selected, saving, title, description, onSelect }: TopologyCardProps) {
  return (
    <button
      onClick={() => onSelect(value)}
      disabled={saving}
      className={`flex flex-col gap-2.5 p-4 rounded-2xl border text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
        selected
          ? 'border-emerald-500/40 bg-emerald-500/10'
          : 'border-white/10 bg-white/4 hover:bg-white/7 hover:border-white/20'
      }`}
    >
      <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
        selected ? 'bg-emerald-500/20 text-emerald-400' : 'bg-white/8 text-white/40'
      }`}>
        {saving && selected ? <Loader2 className="w-4 h-4 animate-spin" /> : icon}
      </div>
      <div>
        <p className={`text-xs font-semibold mb-1 ${selected ? 'text-emerald-400' : 'text-white/80'}`}>
          {title}
          {selected && <span className="ml-1.5 text-[10px] font-normal opacity-70">Activo</span>}
        </p>
        <p className="text-white/35 text-[11px] leading-relaxed">{description}</p>
      </div>
    </button>
  )
}

interface MqttCredsFormProps {
  title: string
  description: string
  state: MqttCredsState
  onChange: (s: MqttCredsState) => void
  onSave: () => void
}

function MqttCredsForm({ title, description, state, onChange, onSave }: MqttCredsFormProps) {
  return (
    <div className="border border-white/10 bg-white/4 rounded-2xl p-4 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-white">{title}</p>
          <p className="text-white/35 text-xs mt-0.5">{description}</p>
        </div>
        {state.configured && (
          <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-500/15 text-emerald-400 border border-emerald-500/25 px-2 py-0.5 rounded-full font-medium shrink-0">
            <div className="w-1 h-1 rounded-full bg-emerald-400" />
            Configurado
          </span>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-white/40 text-xs font-medium uppercase tracking-wide">URL del broker</label>
        <input
          type="text"
          value={state.url}
          onChange={e => onChange({ ...state, url: e.target.value, saved: false })}
          placeholder="mqtts://xxxx.s1.eu.hivemq.cloud:8883"
          className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent font-mono transition-all"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-white/40 text-xs font-medium uppercase tracking-wide">Usuario</label>
        <input
          type="text"
          value={state.username}
          onChange={e => onChange({ ...state, username: e.target.value, saved: false })}
          className="bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent transition-all"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="text-white/40 text-xs font-medium uppercase tracking-wide">Contraseña</label>
        <div className="relative">
          <input
            type={state.showPassword ? 'text' : 'password'}
            value={state.password}
            onChange={e => onChange({ ...state, password: e.target.value, saved: false })}
            className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 pr-9 text-sm text-white placeholder:text-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent font-mono transition-all"
          />
          <button
            type="button"
            onClick={() => onChange({ ...state, showPassword: !state.showPassword })}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70 transition-colors"
          >
            {state.showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
        </div>
      </div>

      {state.error && (
        <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-lg px-2.5 py-1.5">{state.error}</p>
      )}
      {state.saved && (
        <div className="flex items-center gap-1.5 text-emerald-400 text-xs bg-emerald-500/10 border border-emerald-500/20 rounded-lg px-2.5 py-1.5">
          <CheckCircle className="w-3 h-3" />
          Guardado
        </div>
      )}

      <button
        onClick={onSave}
        disabled={state.saving || !state.url.trim() || !state.username.trim() || !state.password.trim()}
        className="flex items-center justify-center gap-1.5 text-white text-xs font-semibold py-2 rounded-xl transition-all active:scale-[0.97] disabled:opacity-40"
        style={{ background: 'linear-gradient(135deg, #2563eb, #4f46e5)' }}
      >
        {state.saving && <Loader2 className="w-3 h-3 animate-spin" />}
        Guardar
      </button>
    </div>
  )
}

interface CollapsibleSectionProps {
  icon: React.ReactNode
  title: string
  open: boolean
  onToggle: () => void
  children: React.ReactNode
}

function CollapsibleSection({ icon, title, open, onToggle, children }: CollapsibleSectionProps) {
  return (
    <div className={`border rounded-2xl overflow-hidden transition-all ${
      open ? 'border-white/15 bg-white/4' : 'border-white/8 bg-white/3 hover:bg-white/5'
    }`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-5 py-4 transition-colors"
      >
        <div className="flex items-center gap-2.5 text-white/70">
          {icon}
          <span className="text-sm font-medium">{title}</span>
        </div>
        {open
          ? <ChevronUp className="w-4 h-4 text-white/30" />
          : <ChevronDown className="w-4 h-4 text-white/30" />
        }
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-white/8">
          {children}
        </div>
      )}
    </div>
  )
}
